/**
 * Local Whisper Transcription
 *
 * Runs Whisper model in the browser using transformers.js.
 * Provides zero-latency, offline transcription with automatic model caching.
 */

import { pipeline, AutomaticSpeechRecognitionPipeline } from '@xenova/transformers';

/** Whisper model sizes available */
export type WhisperModelSize = 'tiny' | 'base' | 'small';

/** Model configurations with sizes */
const MODEL_CONFIGS: Record<WhisperModelSize, { id: string; size: string }> = {
  tiny: { id: 'Xenova/whisper-tiny', size: '39MB' },
  base: { id: 'Xenova/whisper-base', size: '74MB' },
  small: { id: 'Xenova/whisper-small', size: '244MB' },
};

/** In-memory cached model pipeline */
let cachedPipeline: AutomaticSpeechRecognitionPipeline | null = null;
let currentModelSize: WhisperModelSize | null = null;

/** Progress callback type */
export type ProgressCallback = (progress: number) => void;

/** Model load result */
export interface ModelLoadResult {
  progress: number;
  loaded: boolean;
}

/**
 * Check if Whisper is supported in this browser
 * Requires WebAssembly and modern browser features
 */
export function isWhisperSupported(): boolean {
  try {
    // Check for WebAssembly support
    if (typeof WebAssembly !== 'object') {
      return false;
    }

    // Check for necessary browser APIs
    if (!window.AudioContext && !(window as any).webkitAudioContext) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Load the Whisper model into memory
 * Uses IndexedDB caching automatically - model downloads only once
 *
 * @param modelSize - Model size to load (tiny/base/small)
 * @param onProgress - Optional progress callback (0-100)
 * @returns Promise resolving when model is loaded
 */
export async function loadWhisperModel(
  modelSize: WhisperModelSize = 'tiny',
  onProgress?: ProgressCallback
): Promise<ModelLoadResult> {
  // If model already loaded with same size, return immediately
  if (cachedPipeline && currentModelSize === modelSize) {
    onProgress?.(100);
    return { progress: 100, loaded: true };
  }

  const modelConfig = MODEL_CONFIGS[modelSize];

  try {
    // transformers.js handles caching automatically via IndexedDB
    // It will download only on first use, then load from cache
    cachedPipeline = await pipeline(
      'automatic-speech-recognition',
      modelConfig.id,
      {
        // Progress callback for model downloads
        progress_callback: (data: { progress?: number; status?: string }) => {
          if (data.progress !== undefined) {
            // transformers.js reports progress as 0-100
            onProgress?.(Math.round(data.progress));
          }
        },
      }
    );

    currentModelSize = modelSize;
    onProgress?.(100);

    return { progress: 100, loaded: true };
  } catch (error) {
    cachedPipeline = null;
    currentModelSize = null;
    throw new Error(
      `Failed to load Whisper model (${modelSize}): ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Transcribe audio using local Whisper model
 * Model must be loaded first via loadWhisperModel()
 *
 * @param audioBlob - Audio data to transcribe
 * @param options - Optional transcription settings
 * @returns Transcribed text
 */
export async function transcribeLocal(
  audioBlob: Blob,
  options?: { language?: string; prompt?: string }
): Promise<string> {
  if (!cachedPipeline) {
    throw new Error('Whisper model not loaded. Call loadWhisperModel() first.');
  }

  try {
    // Decode encoded audio (webm/mp4/ogg) to raw PCM Float32Array
    // MediaRecorder outputs encoded audio — Whisper needs raw 16kHz mono PCM
    const arrayBuffer = await audioBlob.arrayBuffer();
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioCtx({ sampleRate: 16000 });
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const audioData = audioBuffer.getChannelData(0); // mono Float32Array
    audioContext.close();

    // Run transcription
    const result = await cachedPipeline(audioData, {
      language: options?.language, // ISO-639-1 code (e.g., 'en', 'es')
      // Note: transformers.js doesn't support prompt parameter yet
      // This is a limitation of the library
    });

    // Extract text from result (handle both single result and array)
    const singleResult = Array.isArray(result) ? result[0] : result;
    const text = typeof singleResult === 'string' ? singleResult : singleResult?.text;

    if (!text || typeof text !== 'string') {
      throw new Error('Transcription returned invalid result');
    }

    return text;
  } catch (error) {
    throw new Error(
      `Local transcription failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Get current model info
 */
export function getModelInfo(): { size: WhisperModelSize | null; loaded: boolean } {
  return {
    size: currentModelSize,
    loaded: cachedPipeline !== null,
  };
}

/**
 * Unload the model from memory (frees up RAM)
 * Model remains cached in IndexedDB for fast reload
 */
export function unloadModel(): void {
  cachedPipeline = null;
  currentModelSize = null;
}
